/**
 * Post-auth return-to destination.
 *
 * When an unauthenticated user follows a deep link (e.g. a shared ritual or
 * community URL) they are sent through login or the signup -> verify ->
 * onboarding -> welcome chain. We persist the originally intended path so the
 * final step can return them there instead of dropping them on the home page.
 *
 * sessionStorage (not router state) is used deliberately: the chain includes
 * full-page reloads (magic-link `window.location.href`, OAuth round-trips) that
 * would discard in-memory router state. A single key serves every auth entry
 * point (protected routes, login modal, OAuth callback).
 */

const RETURN_TO_KEY = "auth_return_to"

// Bound how long a captured destination stays valid so an abandoned auth flow
// can't redirect a later, unrelated login elsewhere in the same tab session.
// Generous enough to cover an email-verification round-trip (checking inbox).
const RETURN_TO_TTL_MS = 60 * 60 * 1000

// Paths that must never become a post-auth destination -- returning to them
// would loop the user back into the auth flow or land on a useless screen.
const EXCLUDED_PREFIXES = [
  "/login",
  "/signup",
  "/verify-email",
  "/onboarding",
  "/welcome",
  "/forgot-password",
  "/reset-password",
  "/oauth",
]

interface StoredReturnTo {
  p: string
  t: number
}

/**
 * A path is restorable only if it is a same-origin, in-app, non-auth route.
 *
 * Security: the value is read back from sessionStorage (attacker-writable under
 * XSS) and fed to `navigate()`, so this is the open-redirect guard. It requires
 * a leading "/" (rejects absolute/`javascript:` URLs) and resolves against the
 * current origin, rejecting anything cross-origin -- which catches protocol-
 * relative (`//evil.com`), backslash (`/\evil.com`, which browsers normalize to
 * a host), and other normalization tricks in one check. The home route is
 * rejected too (no point storing the default).
 */
export function isRestorableReturnTo(path: string | null | undefined): path is string {
  if (!path?.startsWith("/")) return false
  let pathname: string
  try {
    const url = new URL(path, window.location.origin)
    if (url.origin !== window.location.origin) return false
    pathname = url.pathname
  } catch {
    return false
  }
  if (pathname === "/") return false
  return !EXCLUDED_PREFIXES.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`))
}

/** Remember where to return after auth completes. No-op for non-restorable paths. */
export function saveAuthReturnTo(path: string): void {
  if (isRestorableReturnTo(path)) {
    const entry: StoredReturnTo = { p: path, t: Date.now() }
    sessionStorage.setItem(RETURN_TO_KEY, JSON.stringify(entry))
  }
}

/**
 * Read and clear the stored destination. Returns null when nothing valid is
 * stored (missing, expired, malformed, or no longer restorable), so callers can
 * `consumeAuthReturnTo() ?? "/"`.
 */
export function consumeAuthReturnTo(): string | null {
  const raw = sessionStorage.getItem(RETURN_TO_KEY)
  sessionStorage.removeItem(RETURN_TO_KEY)
  if (!raw) return null
  try {
    const { p, t } = JSON.parse(raw) as Partial<StoredReturnTo>
    if (typeof p !== "string" || typeof t !== "number") return null
    if (Date.now() - t > RETURN_TO_TTL_MS) return null
    return isRestorableReturnTo(p) ? p : null
  } catch {
    return null
  }
}

/** Capture the current location (path + query) as the return-to destination. */
export function saveCurrentLocationAsReturnTo(): void {
  saveAuthReturnTo(window.location.pathname + window.location.search)
}

/**
 * After login/OAuth, the stored return-to must only be honored once the user is
 * past onboarding. While profile creation OR onboarding is still pending, the
 * return-to is left in place so the welcome step (the end of onboarding) can
 * restore it. Consuming it earlier sends the user to the deep link, an
 * onboarding guard then bounces them into onboarding, and by the time they reach
 * "Start Exploring" the return-to is already gone -- stranding them on home
 * (issue #565). The login pages already gate on onboarding; this lets the OAuth
 * callback do the same.
 */
export function shouldRunOnboardingBeforeReturn(
  profileRequired: boolean,
  onboardingCompleted: boolean,
): boolean {
  return profileRequired || !onboardingCompleted
}

// One-shot, path-scoped marker: set when the welcome step sends a brand-new user
// to their deep link instead of the default onboarding destination. The
// destination page (e.g. a ritual) reads it to DEFER its auto-starting tour for
// that one visit, so the tour doesn't redirect the user off the page they were
// sent to. The home tour still runs on their next Home visit (issue #565).
//
// The marker stores the destination PATH so a stale marker (e.g. from a community
// deep link that never consumed it) can't wrongly defer the tour on a later,
// unrelated page -- consume only returns true when the current path matches.
const DEEP_LINK_LANDING_KEY = "deep_link_landing"

/** Mark `destinationPath` as a deep-link landing (defer that page's auto tour). */
export function markDeepLinkLanding(destinationPath: string): void {
  // Store only the pathname; the destination page is identified by path, not query.
  sessionStorage.setItem(DEEP_LINK_LANDING_KEY, destinationPath.split(/[?#]/)[0])
}

/**
 * Read and clear the marker. Returns true only when the CURRENT page is the
 * marked deep-link destination, so a stale marker is cleared without deferring an
 * unrelated page's tour.
 */
export function consumeDeepLinkLanding(): boolean {
  const stored = sessionStorage.getItem(DEEP_LINK_LANDING_KEY)
  sessionStorage.removeItem(DEEP_LINK_LANDING_KEY)
  return stored !== null && stored === window.location.pathname
}
